import { Typography } from "@suraasa/placebo-ui"
import clsx from "clsx"
import { useEffect, useRef } from "react"

import { Tab, Tabs as TabsType } from "./hooks/useTabs"
import styles from "./TabBar.module.css"
import TabItem from "./TabItem"

type Props = {
  tabs: TabsType
  activeTab: Tab
  onChange: (newTab: string) => void
  className?: string
  getOnboardingTarget?: (tab: Tab) => string | undefined
}

const TabBar = ({
  className,
  tabs,
  activeTab,
  onChange,
  getOnboardingTarget,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)

  // Keep the active tab visible when the tab strip overflows horizontally.
  // Only scrolls the strip itself, never the page.
  useEffect(() => {
    const container = containerRef.current
    const activeEl = container?.querySelector<HTMLElement>(
      "[data-active='true']"
    )
    if (!container || !activeEl) return

    const { paddingLeft, paddingRight } = getComputedStyle(container)
    const containerRect = container.getBoundingClientRect()
    const activeRect = activeEl.getBoundingClientRect()
    const visibleLeft = containerRect.left + parseFloat(paddingLeft)
    const visibleRight = containerRect.right - parseFloat(paddingRight)

    if (activeRect.left < visibleLeft) {
      container.scrollBy({
        left: activeRect.left - visibleLeft,
        behavior: "smooth",
      })
    } else if (activeRect.right > visibleRight) {
      container.scrollBy({
        left: activeRect.right - visibleRight,
        behavior: "smooth",
      })
    }
  }, [activeTab.name])

  const getTabTitle = (tab: Tab) => {
    return (
      <Typography
        variant={tab.name === activeTab.name ? "strong" : "body"}
        className={clsx("inline-flex items-center gap-0.5")}
      >
        {tab.name}{" "}
        {Boolean(tab.count) && (
          <span
            style={{
              fontWeight: 500,
              fontSize: 12,
              lineHeight: "130%",
            }}
            className={clsx("rounded-md px-0.5 py-0.25", {
              "bg-secondary-900 text-surface-500": tab.name === activeTab.name,
              "bg-onSurface-200 text-onSurface-500":
                tab.name !== activeTab.name,
            })}
          >
            {tab.count}
          </span>
        )}
      </Typography>
    )
  }

  return (
    <div ref={containerRef} className={clsx(styles.tabContainer, className)}>
      {tabs.map(tab => {
        return (
          <TabItem
            isActive={tab.name === activeTab.name}
            data-active={tab.name === activeTab.name}
            key={tab.name}
            onClick={() => onChange(tab.name)}
            data-tour={getOnboardingTarget?.(tab)}
            className={tab.target}
          >
            {tab.startAdornment ? (
              <div className="flex items-center gap-1">
                <>
                  {tab.startAdornment}
                  {getTabTitle(tab)}
                </>
              </div>
            ) : (
              getTabTitle(tab)
            )}
          </TabItem>
        )
      })}
    </div>
  )
}

export default TabBar
